import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  css,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { AU_STATES, US_STATES } from './constants';
import { LoadingButton } from '@mui/lab';
import { staticFileLocation } from '@server/constants';
import { WotcSurveyData } from './types';
import { usePostWotcSurvey } from '@features/wotc/hooks/usePostWotcSurvey';
import { toast } from 'react-toastify';
import { useUserCountry } from '@context/UserCountryContext';

type TanfSurveyFormsModalProps = {
  tanfRecipientFormModal: boolean;
  handleClose: () => void;
  wotcSurveyData: WotcSurveyData;
};
const defaultValues = {
  tanf1: 'Y',
  tanf1a: 'N',
  tanf1a_recipient_first_name: '',
  tanf1a_recipient_last_name: '',
  tanf1a_recipient_city: '',
  tanf1a_recipient_state: '',
};

const TanfSurveyFormsModal: FC<TanfSurveyFormsModalProps> = ({
  tanfRecipientFormModal,
  wotcSurveyData,
  handleClose,
}) => {
  const { userCountry } = useUserCountry();
  const states = (userCountry === 'au') ? AU_STATES : US_STATES;

  const [errorText, setErrorText] = useState('');
  const { sendWotcSurvey, isSendingWotcSurvey } = usePostWotcSurvey({
    setErrorText,
  });
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    register,
    setValue,
  } = useForm({
    defaultValues,
  });

  // Watch form fields for conditional rendering
  const tanf1 = watch('tanf1');

  const tanf1a = watch('tanf1a');

  const onSubmit = (data: any) => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        toast.success('Data submitted successfully');
        handleClose();
      },
    };
    const transformedData = {
      ...data,
      status: 3,
    };
    sendWotcSurvey({ ...transformedData }, options);
  };

  useEffect(() => {
    if (wotcSurveyData) {
      Object.keys(defaultValues).forEach((key) => {
        const typedKey = key as keyof typeof defaultValues &
          keyof WotcSurveyData;
        if (wotcSurveyData[typedKey] !== undefined) {
          if (wotcSurveyData[typedKey] !== null) {
            setValue(typedKey, wotcSurveyData[typedKey]);
          }
        }
      });
    }
  }, [wotcSurveyData, setValue]);

  return (
    <Dialog
      open={tanfRecipientFormModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap="5px"
        >
          <img
            src={`${staticFileLocation}img/images/icon4.png`}
            alt="Logo"
            css={css`
              max-height: 60px;
              display: block;
            `}
          />
          <Typography variant="h5">
            Temporary Assistance For Needy Families (TANF)
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errorText && (
            <Alert sx={{ mb: 1 }} severity="error">
              {errorText}
            </Alert>
          )}
          {/* Question 1 */}
          <FormControl component="fieldset" margin="normal">
            <Box
              sx={{
                display: 'flex',
                alignItems: {
                  xs: 'start',
                  sm: 'center',
                },
                justifyContent: 'space-between',
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                gap: '4px',
              }}
            >
              <FormLabel component="legend">
                Are you a member of a family that received TANF assistance for
                any 9 months during the 18-month period before you were hired?
              </FormLabel>
              <Controller
                name="tanf1"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row sx={{ flexWrap: 'nowrap' }}>
                    <FormControlLabel
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                    <FormControlLabel
                      value="U"
                      control={<Radio />}
                      label="Unsure"
                    />
                  </RadioGroup>
                )}
              />
            </Box>
          </FormControl>

          {/* Question 2 - Conditional */}
          {tanf1 === 'Y' && (
            <FormControl component="fieldset" margin="normal">
              <Box
                mb={2}
                sx={{
                  display: 'flex',
                  alignItems: {
                    xs: 'start',
                    sm: 'center',
                  },
                  justifyContent: 'space-between',
                  flexDirection: {
                    xs: 'column',
                    sm: 'row',
                  },
                  gap: '4px',
                }}
              >
                <FormLabel component="legend">
                  Are you a member of a family that received TANF assistance for
                  at least the last 18 months before you were hired?
                </FormLabel>
                <Controller
                  name="tanf1a"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row sx={{ flexWrap: 'nowrap' }}>
                      <FormControlLabel
                        value="Y"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="N"
                        control={<Radio />}
                        label="No"
                      />
                      <FormControlLabel
                        value="U"
                        control={<Radio />}
                        label="Unsure"
                      />
                    </RadioGroup>
                  )}
                />
              </Box>
            </FormControl>
          )}

          {/* Recipient Details - Conditional */}
          {tanf1a === 'Y' && (
            <>
              <Stack
                spacing={2}
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                mb={3}
              >
                <TextField
                  label="Enter the first name of the primary recipient"
                  variant="outlined"
                  fullWidth
                  size="small"
                  margin="normal"
                  {...register('tanf1a_recipient_first_name', {
                    required: 'First name is required',
                  })}
                  error={!!errors.tanf1a_recipient_first_name}
                  helperText={errors.tanf1a_recipient_first_name?.message}
                />

                <TextField
                  label="Enter last name of the recipient"
                  variant="outlined"
                  fullWidth
                  size="small"
                  margin="normal"
                  {...register('tanf1a_recipient_last_name', {
                    required: 'Last name is required',
                  })}
                  error={!!errors.tanf1a_recipient_last_name}
                  helperText={errors.tanf1a_recipient_last_name?.message}
                />
              </Stack>
              <Stack
                spacing={2}
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                mb={2}
              >
                <TextField
                  label="Enter the city where the benefits were received"
                  variant="outlined"
                  fullWidth
                  size="small"
                  margin="normal"
                  {...register('tanf1a_recipient_city', {
                    required: 'City is required',
                  })}
                  error={!!errors.tanf1a_recipient_city}
                  helperText={errors.tanf1a_recipient_city?.message}
                />

                <FormControl fullWidth margin="normal">
                  <Select
                    value={watch('tanf1a_recipient_state') || ''} // watch for the field's value
                    size="small"
                    displayEmpty
                    {...register('tanf1a_recipient_state', {
                      required: 'State is required',
                    })}
                    error={!!errors.tanf1a_recipient_state}
                  >
                    <MenuItem value="" disabled>
                      Select a state
                    </MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state.code} value={state.code}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.tanf1a_recipient_state && (
                    <span
                      style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                    >
                      {errors.tanf1a_recipient_state?.message}
                    </span>
                  )}
                </FormControl>
              </Stack>
            </>
          )}

          <Box width="100%" textAlign="center">
            <LoadingButton
              sx={{ mt: 3, width: '100%' }}
              loading={isSendingWotcSurvey}
              type="submit"
              variant="contained"
              color="primary"
            >
              Submit
            </LoadingButton>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TanfSurveyFormsModal;
